/** Field validation — ARCHITECTURE §6.9. Server-side authority; clients mirror for instant feedback. */
import { F_MAX_S, F_MIN_S } from '../constants.js';
import type { Gtl, Solution, Target } from './types.js';

export interface ValidationResult<T> {
  ok: boolean;
  value?: T;
  errors: Record<string, string>;
  warnings: string[];
}

const MGRS_RE = /^(60|[1-5][0-9]|[1-9])[C-HJ-NP-X][A-HJ-Z][A-HJ-V](\d{2}|\d{4}|\d{6}|\d{8}|\d{10})$/;
const NAME_RE = /^[A-Z0-9][A-Z0-9 _-]{0,15}$/;

export function validateTarget(input: unknown): ValidationResult<Target> {
  const errors: Record<string, string> = {};
  const t = (input ?? {}) as { type?: unknown; value?: unknown };
  if (t.type !== 'MGRS' && t.type !== 'NAME') errors['target.type'] = 'must be MGRS or NAME';
  if (typeof t.value !== 'string' || t.value.trim() === '') errors['target.value'] = 'required';
  if (Object.keys(errors).length) return { ok: false, errors, warnings: [] };
  const raw = t.value as string;
  if (t.type === 'MGRS') {
    const v = raw.replace(/\s+/g, '').toUpperCase();
    if (!MGRS_RE.test(v)) return { ok: false, errors: { 'target.value': 'not a valid MGRS reference' }, warnings: [] };
    return { ok: true, value: { type: 'MGRS', value: v }, errors: {}, warnings: [] };
  }
  const v = raw.trim().toUpperCase();
  if (!NAME_RE.test(v)) return { ok: false, errors: { 'target.value': 'target name: 1–16 chars A–Z 0–9 space _ -' }, warnings: [] };
  return { ok: true, value: { type: 'NAME', value: v }, errors: {}, warnings: [] };
}

export function validateExpectedMin(input: unknown): ValidationResult<number> {
  const n = typeof input === 'number' ? input : Number.NaN;
  if (!Number.isInteger(n) || n < 1 || n > 180) return { ok: false, errors: { expectedMin: 'integer 1–180' }, warnings: [] };
  return { ok: true, value: n, errors: {}, warnings: [] };
}

const MILS_PER_DEG = 6400 / 360;

function round1(x: number): number {
  return Math.round(x * 10) / 10;
}

export function validateGtl(input: unknown): ValidationResult<Gtl | null> {
  if (input === null || input === undefined) return { ok: true, value: null, errors: {}, warnings: [] };
  const g = input as { unit?: unknown; value?: unknown };
  const value = typeof g.value === 'number' ? g.value : Number.NaN;
  if (g.unit === 'mils') {
    if (!(value >= 0 && value <= 6399.9) || round1(value) !== value) return { ok: false, errors: { gtl: 'mils 0–6399.9 in 0.1 steps' }, warnings: [] };
    return { ok: true, value: { unit: 'mils', value, mils: value }, errors: {}, warnings: [] };
  }
  if (g.unit === 'deg') {
    if (!(value >= 0 && value <= 359.9) || round1(value) !== value) return { ok: false, errors: { gtl: 'degrees 0–359.9 in 0.1 steps' }, warnings: [] };
    return { ok: true, value: { unit: 'deg', value, mils: round1(value * MILS_PER_DEG) }, errors: {}, warnings: [] };
  }
  return { ok: false, errors: { gtl: 'unit must be mils or deg' }, warnings: [] };
}

export function inPrfBand(code: string): boolean {
  if (!/^[1-8]{4}$/.test(code)) return false;
  const n = Number(code);
  return n >= 1111 && n <= 1788;
}

export function validateSolution(input: unknown, opts: { strictPrfBand?: boolean } = {}): ValidationResult<Solution> {
  const errors: Record<string, string> = {};
  const warnings: string[] = [];
  const s = (input ?? {}) as { gtl?: unknown; laserCode?: unknown; shellFlightTimeS?: unknown; laserOnLeadS?: unknown; prfOverride?: unknown };
  const gtl = validateGtl(s.gtl);
  Object.assign(errors, gtl.errors);
  const code = typeof s.laserCode === 'string' ? s.laserCode : '';
  if (!/^\d{4}$/.test(code)) errors['laserCode'] = '4 digits';
  else if (opts.strictPrfBand && !inPrfBand(code)) {
    if (s.prfOverride === true) warnings.push(`laser code ${code} outside the PRF band 1111–1788 (override recorded)`);
    else errors['laserCode'] = 'outside the PRF band 1111–1788 — confirm override';
  }
  const F = typeof s.shellFlightTimeS === 'number' ? s.shellFlightTimeS : Number.NaN;
  const L = typeof s.laserOnLeadS === 'number' ? s.laserOnLeadS : Number.NaN;
  if (!(F >= F_MIN_S && F <= F_MAX_S) || round1(F) !== F) errors['shellFlightTimeS'] = `${F_MIN_S.toFixed(1)}–${F_MAX_S.toFixed(1)} s in 0.1 steps`;
  if (!(L >= 1.0 && L <= 60.0) || round1(L) !== L) errors['laserOnLeadS'] = '1.0–60.0 s in 0.1 steps';
  if (!errors['shellFlightTimeS'] && !errors['laserOnLeadS']) {
    if (L > F - 1.0) errors['laserOnLeadS'] = 'laser lead must be at least 1.0 s less than the flight time';
    else if (F - L < 5.0) warnings.push('laser cue overlaps the 5 s warn window');
  }
  if (Object.keys(errors).length) return { ok: false, errors, warnings };
  const value: Solution = { gtl: gtl.value ?? null, laserCode: code, shellFlightTimeS: F, laserOnLeadS: L };
  if (s.prfOverride === true) value.prfOverride = true;
  return { ok: true, value, errors: {}, warnings };
}

export function validateNote(input: unknown): ValidationResult<string | null> {
  if (input === undefined || input === null || input === '') return { ok: true, value: null, errors: {}, warnings: [] };
  if (typeof input !== 'string' || input.length > 200) return { ok: false, errors: { note: 'up to 200 characters' }, warnings: [] };
  return { ok: true, value: input, errors: {}, warnings: [] };
}
